import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, MenuItem, Button, Grid, Alert, CircularProgress, Stack, Chip
} from '@mui/material';
import { UploadFile as UploadFileIcon, Description as DocIcon } from '@mui/icons-material';
import { MAX_FILE_BYTES, MAX_FILE_LABEL, oversizeMessage } from '../../utils/fileSizeLimit';


const MAX_TOTAL_BYTES = 10 * 1024 * 1024;
const EMPTY_META = { documentNumber: '', issueDate: '', expiryDate: '' };

/** Upload one or more files as the next version of one company document type. */
const UploadOrgDocumentDialog = ({ open, onClose, api, documentTypes = [], onUploaded }) => {
  const [docType, setDocType] = useState('');
  const [files, setFiles] = useState([]);
  const [meta, setMeta] = useState(EMPTY_META);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reading, setReading] = useState(false);
  const [readNotice, setReadNotice] = useState('');
  const [metadata, setMetadata] = useState(null);

  const reset = () => {
    setDocType('');
    setFiles([]);
    setMeta(EMPTY_META);
    setSaving(false);
    setError('');
    setReading(false);
    setReadNotice('');
    setMetadata(null);
  };

  // Fills only the empty fields from the file; nothing is saved until Upload.
  const readFile = async (file, type) => {
    setReading(true);
    setReadNotice('');
    try {
      const found = await api.extract(file, type);
      const number = found?.documentNumber;
      setMeta((p) => ({
        documentNumber: p.documentNumber || number || '',
        issueDate: p.issueDate || found?.issueDate || '',
        expiryDate: p.expiryDate || found?.expiryDate || ''
      }));
      setMetadata(found?.licence ? JSON.stringify(found.licence) : null);
      setReadNotice((found?.warnings || []).join(' '));
    } catch (err) {
      setReadNotice(`${err?.response?.data?.error || err?.response?.data?.message || 'Could not read the file.'} Enter the details yourself.`);
    } finally {
      setReading(false);
    }
  };

  const handleType = (type) => {
    setDocType(type);
    setMetadata(null);
    if (files.length && type) readFile(files[0], type);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleFiles = (e) => {
    const all = Array.from(e.target.files || []);
    e.target.value = '';
    const tooBig = all.filter((f) => f.size > MAX_FILE_BYTES);
    const accepted = [];
    let total = files.reduce((sum, f) => sum + f.size, 0);
    let overTotal = false;
    all.filter((f) => f.size <= MAX_FILE_BYTES).forEach((f) => {
      if (total + f.size > MAX_TOTAL_BYTES) { overTotal = true; return; }
      total += f.size;
      accepted.push(f);
    });
    const messages = tooBig.map(oversizeMessage);
    if (overTotal) messages.push('The files together can be at most 10 MB, so some were not added.');
    setError(messages.join(' '));
    setFiles((prev) => [...prev, ...accepted]);
    if (!files.length && accepted.length && docType) readFile(accepted[0], docType);
  };

  const removeFile = (idx) => {
    const remaining = files.filter((_, i) => i !== idx);
    setFiles(remaining);
    if (idx !== 0) return;
    setMeta(EMPTY_META);
    setMetadata(null);
    setReadNotice('');
    if (remaining.length && docType) readFile(remaining[0], docType);
  };

  const handleSubmit = async () => {
    setError('');
    if (!docType) { setError('Choose a document type.'); return; }
    if (!files.length) { setError('Choose at least one file.'); return; }
    if (meta.issueDate && meta.expiryDate && meta.expiryDate < meta.issueDate) {
      setError('Expiry date cannot be before the issue date.');
      return;
    }
    setSaving(true);
    try {
      const resp = await api.upload(files, docType, {
        documentNumber: meta.documentNumber.trim() || null,
        issueDate: meta.issueDate || null,
        expiryDate: meta.expiryDate || null,
        metadata: metadata || undefined
      });
      if (resp?.data?.success === false) throw new Error(resp.data.error || 'Upload failed');
      onUploaded();
      reset();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to upload the document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DocIcon color="secondary" />
        Upload Company Document
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {reading && <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 2 }}>Reading the document…</Alert>}
        {readNotice && !reading && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setReadNotice('')}>{readNotice}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField select label="Document type" value={docType} onChange={(e) => handleType(e.target.value)}
              fullWidth size="small" disabled={saving}>
              {documentTypes.length === 0 && <MenuItem value="" disabled>No company document types available</MenuItem>}
              {documentTypes.map((dt) => (
                <MenuItem key={dt.code} value={dt.code} sx={{ fontSize: '0.85rem' }}>{dt.label || dt.code}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
              <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} disabled={saving}>
                Choose files
                <input type="file" hidden multiple accept="image/*,.pdf" onChange={handleFiles} />
              </Button>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                PDF, JPG, PNG or WEBP. Up to 10 files, {MAX_FILE_LABEL} each and 10 MB in total. They are saved together as the current version.
              </Typography>
              {files.length > 0 && (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  {files.map((f, idx) => (
                    <Chip key={`${f.name}-${f.size}-${idx}`} size="small" label={f.name} disabled={saving}
                      onDelete={() => removeFile(idx)} />
                  ))}
                </Stack>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Document number" value={meta.documentNumber} disabled={saving}
              onChange={(e) => setMeta((p) => ({ ...p, documentNumber: e.target.value }))} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Issue date" type="date" value={meta.issueDate} InputLabelProps={{ shrink: true }} disabled={saving}
              onChange={(e) => setMeta((p) => ({ ...p, issueDate: e.target.value }))} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Expiry date" type="date" value={meta.expiryDate} InputLabelProps={{ shrink: true }} disabled={saving}
              onChange={(e) => setMeta((p) => ({ ...p, expiryDate: e.target.value }))} fullWidth size="small" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || reading}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadOrgDocumentDialog;
